import { Link } from "react-router-dom";
import { Mail, Globe, MapPin, Phone, MessageCircle } from "lucide-react";
import { SERVICES } from "@/data/services";
import TriAxisLogo from "./TriAxisLogo";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0B1220] text-slate-300 border-t border-[#1F2A44]"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* Brand */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 group">
            <TriAxisLogo size={44} testid="footer-logo-icon" />
            <div className="leading-none" style={{ display: "grid", gridTemplateColumns: "auto" }}>
              <div className="display text-[32px] font-extrabold tracking-tight text-white whitespace-nowrap">
                Tri-Axis <span className="text-emerald-400">XYZ</span>
              </div>
              <p
                className="mono text-[11px] uppercase text-slate-500 mt-1.5"
                style={{ textAlign: "justify", textAlignLast: "justify" }}
              >
                Survey CAD Solutions
              </p>
            </div>
          </div>
          <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-sm">
            We deliver precision CAD drafting and land-survey production
            services for surveying firms across the USA, Canada and Australia.
          </p>
          <ul className="mt-6 space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-emerald-400" />
              <a
                href="mailto:triaxisxyzsolutions@gmail.com"
                data-testid="footer-email"
                className="hover:text-white"
              >
                triaxisxyzsolutions@gmail.com
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-500" />
              <a
                href="mailto:contact@triaxisxyz.com"
                data-testid="footer-email-alt"
                className="hover:text-white text-slate-400"
              >
                contact@triaxisxyz.com
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-emerald-400" />
              <span>triaxisxyz.com</span>
            </li>
            <li className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-emerald-400" />
              <span>Orlando, Florida · USA</span>
            </li>
            <li className="flex items-start gap-2">
              <Phone className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
              <div className="flex flex-col">
                <a
                  href="tel:+918083691588"
                  data-testid="footer-phone"
                  className="hover:text-white font-semibold"
                >
                  +91 80836 91588
                </a>
                <span className="mono text-[9px] tracking-[0.22em] uppercase text-emerald-400/90 mt-0.5 inline-flex items-center gap-1">
                  <MessageCircle className="h-2.5 w-2.5" /> WhatsApp · Call · Text
                </span>
              </div>
            </li>
          </ul>
        </div>

        {/* Services */}
        <div className="md:col-span-5">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">Services</div>
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`footer-service-${s.slug}`}
                  className="text-slate-400 hover:text-white"
                >
                  <span className="mr-1">{s.emoji}</span>{s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Company */}
        <div className="md:col-span-3">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">Company</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/" className="text-slate-400 hover:text-white">Home</Link></li>
            <li><Link to="/survey-techies" className="text-slate-400 hover:text-white">Survey Techies</Link></li>
            <li><Link to="/cad-techies" data-testid="footer-cad-techies" className="text-slate-400 hover:text-white">CAD Techies</Link></li>
            <li><Link to="/services" className="text-slate-400 hover:text-white">All Services</Link></li>
            <li><Link to="/cad-services" data-testid="footer-coverage" className="text-slate-400 hover:text-white">Coverage · 50 States</Link></li>
            <li><Link to="/disciplines" data-testid="footer-disciplines" className="text-slate-400 hover:text-white">Disciplines · BIM · MEP · Piping</Link></li>
            <li><Link to="/blog" data-testid="footer-blog" className="text-slate-400 hover:text-white">Blog & Guides</Link></li>
            <li><Link to="/about" className="text-slate-400 hover:text-white">About Us</Link></li>
            <li><Link to="/contact" className="text-slate-400 hover:text-white">Contact</Link></li>
          </ul>
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mt-8">Regions</div>
          <ul className="mt-3 space-y-1 text-sm text-slate-400">
            <li>🇺🇸 United States</li>
            <li>🇨🇦 Canada</li>
            <li>🇦🇺 Australia</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#1F2A44]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-slate-500">
          <div className="mono tracking-[0.16em] uppercase">
            © {new Date().getFullYear()} Tri-Axis XYZ CAD Solutions, LLC · All rights reserved.
          </div>
          <div className="flex items-center gap-5 mono tracking-[0.16em] uppercase">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-slate-200">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-slate-200">Terms</Link>
            <span>ISO-grade workflow</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
